import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Ban, Trash2, Plus, Phone, Mail, RefreshCw, ShieldAlert } from 'lucide-react';

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminBlockedGuests() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ phone: '', email: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/blocked-guests');
      setList(r.data?.blocked || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error('Bitte eine Telefonnummer oder E-Mail angeben.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/blocked-guests', {
        phone: form.phone.trim(),
        email: form.email.trim(),
        reason: form.reason.trim(),
      });
      toast.success('Gast gesperrt.');
      setForm({ phone: '', email: '', reason: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    const label = entry.phone || entry.email || 'diesen Gast';
    if (!window.confirm(`Sperre für „${label}" aufheben?`)) return;
    setDeletingId(entry.id);
    try {
      await api.delete(`/blocked-guests/${entry.id}`);
      setList((prev) => prev.filter((x) => x.id !== entry.id));
      toast.success('Sperre aufgehoben.');
    } catch {
      toast.error('Konnte nicht gelöscht werden.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="admin-blocked-guests">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19] flex items-center gap-2">
            <Ban className="w-7 h-7 text-[#DC2626]" /> Gesperrte Gäste
          </h1>
          <p className="text-sm text-[#5C5C59] mt-1">Gesperrte Telefonnummern & E-Mails können online nicht reservieren. Im Admin erscheint eine rote Warnung.</p>
        </div>
        <button
          onClick={load}
          data-testid="blocked-refresh"
          className="inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition"
        >
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      {/* Neuer Eintrag */}
      <form onSubmit={submit} data-testid="blocked-form" className="bg-white border border-[#E5E5E0] rounded-lg p-4 mb-6">
        <p className="text-sm font-medium text-[#1C1C19] mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Gast sperren
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59]">Telefonnummer</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="z. B. 0171 1234567"
              data-testid="blocked-input-phone"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/10"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59]">E-Mail</label>
            <input
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="z. B. gast@example.com"
              data-testid="blocked-input-email"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/10"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59]">Grund (intern, optional)</label>
            <input
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="z. B. mehrfach No-Show"
              data-testid="blocked-input-reason"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/10"
            />
          </div>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2">Mindestens Telefonnummer oder E-Mail angeben. Sperrt beide Kanäle, wenn beide gesetzt sind.</p>
        <div className="mt-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="blocked-submit"
            className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2.5 rounded text-sm hover:bg-[#B91C1C] transition disabled:opacity-50"
          >
            <Ban className="w-4 h-4" /> {saving ? 'Wird gesperrt…' : 'Gast sperren'}
          </button>
        </div>
      </form>

      {/* Liste */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="blocked-table">
            <thead>
              <tr className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#5C5C59]">
                <th className="px-4 py-3">Telefon</th>
                <th className="px-4 py-3">E-Mail</th>
                <th className="px-4 py-3">Grund</th>
                <th className="px-4 py-3">Gesperrt am</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9CA3AF]">Wird geladen…</td></tr>
              )}
              {!loading && list.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#9CA3AF]" data-testid="blocked-empty">
                  Keine gesperrten Gäste.
                </td></tr>
              )}
              {!loading && list.map((entry) => (
                <tr key={entry.id} className="border-t border-[#F1F1EE] hover:bg-[#F9F9F7] transition" data-testid={`blocked-row-${entry.id}`}>
                  <td className="px-4 py-3 font-mono text-[#1C1C19]">
                    {entry.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-[#9CA3AF]" />{entry.phone}</span> : <span className="text-[#D1D5DB]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#1C1C19]">
                    {entry.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-[#9CA3AF]" />{entry.email}</span> : <span className="text-[#D1D5DB]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#5C5C59]">{entry.reason || <span className="text-[#D1D5DB]">—</span>}</td>
                  <td className="px-4 py-3 text-[#5C5C59]">{fmtDateTime(entry.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(entry)}
                      disabled={deletingId === entry.id}
                      data-testid={`blocked-delete-${entry.id}`}
                      title="Sperre aufheben"
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#E5E5E0] text-[#1C1C19] hover:bg-[#F1F5F9] transition disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Aufheben
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-[#9CA3AF] mt-4 flex items-center gap-1.5">
        <ShieldAlert className="w-3.5 h-3.5" /> Hinweis: Der Abgleich funktioniert unabhängig von Schreibweise/Format (z. B. +49 vs. 0).
      </p>
    </div>
  );
}
